import { useState, useEffect } from "react";
import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { C, card } from "../styles";
import { Btn } from "../ui";
import { REQUIRED_SLOTS, ALL_SLOTS, slotLabel } from "../slotConfig";

function genSessionId(pid) {
  const rand = Math.random().toString(36).slice(2, 9).toUpperCase();
  return `SESS-${pid}-${rand}`;
}

function buildInitialSlots() {
  const slots = {};
  for (const name of ALL_SLOTS) {
    slots[name] = { activeFile: null, history: [] };
  }
  return slots;
}

// Project code map for the submission ID — keep in sync with
// TBD_PROJECT_CODES in functions/index.js.
const PROJECT_CODES = {
  "Cooking": "EH",
  "Proactive Cooking": "PVEH",
  "Appliance data": "EH_AP",
};
function projectCode(project) {
  return PROJECT_CODES[project] || project || "";
}
// Strip a leading "P" so users can enter either "163" or "P163".
function stripPidPrefix(raw) {
  return (raw || "").trim().replace(/^[Pp]/, "");
}

export { genSessionId, buildInitialSlots, REQUIRED_SLOTS, ALL_SLOTS, PROJECT_CODES, projectCode, stripPidPrefix };

const inputStyle = {
  width: "100%",
  background: C.surface,
  border: "1px solid var(--border-2)",
  borderRadius: "9px",
  padding: "11px 14px",
  color: C.text1,
  fontSize: "14px",
  fontWeight: "500",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
  transition: "border-color 0.2s",
};

const labelStyle = {
  display: "block",
  fontSize: "11px",
  fontWeight: "700",
  color: C.text2,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "8px",
};

function SessionCreator({ projects, pid, userName, userEmail, onCreate, getDefaultSubmissionNumber, onOpenOutdoor, initialProject = "" }) {
  const [title, setTitle] = useState("");
  const [project, setProject] = useState(initialProject);
  const [secondCreatorPid, setSecondCreatorPid] = useState("");
  const [secondCreatorError, setSecondCreatorError] = useState("");
  const [submissionNumber, setSubmissionNumber] = useState("");
  const [userEditedNumber, setUserEditedNumber] = useState(false);
  const [creating, setCreating] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Reflect a project chosen on the home project-picker (component may already
  // be mounted when the user navigates in).
  useEffect(() => {
    if (initialProject) setProject(initialProject);
  }, [initialProject]);

  // Outdoor Guide isn't a slot-based session — it has its own uploader section.
  const isOutdoor = project === "Outdoor Guide";
  const canCreate = title.trim() && project && !isOutdoor;

  const handleCreate = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    setSecondCreatorError("");

    const sessionId = genSessionId(pid);
    const slots = buildInitialSlots();
    // Accept either "163" or "P163" — strip the prefix before validating.
    const trimmedSecond = stripPidPrefix(secondCreatorPid) || null;

    const validatePID = httpsCallable(functions, "validatePID");

    // Validate the co-creator PID. Used to credit the co-creator on the
    // task ID; the share toggle additionally sends them a portal invite.
    let resolvedSecond = trimmedSecond;
    if (trimmedSecond) {
      try {
        const { data } = await validatePID({ pid: trimmedSecond });
        resolvedSecond = data.pid;
      } catch (err) {
        setSecondCreatorError(err.message || "Co-creator PID not found.");
        setCreating(false);
        return;
      }
    }
    // Entering a co-creator auto-shares the session with them (adds to
    // sharedWith + sends a pending invite) — no separate opt-in needed.
    const resolvedPartner = resolvedSecond;

    const sessionDoc = {
      sessionId,
      title: title.trim(),
      project,
      creatorPid: pid,
      creatorName: userName,
      creatorEmail: userEmail,
      // Auto-accepted: the co-creator is added straight to partnerPids (not a
      // pending invite), so the session is visible on their dashboard right
      // away — no accept step.
      partnerPids: resolvedPartner ? [resolvedPartner] : [],
      pendingPartnerPids: [],
      sharedWith: resolvedPartner ? [resolvedPartner] : [],
      secondCreatorPid: resolvedSecond || null,
      submissionNumber: submissionNumber ? parseInt(submissionNumber, 10) : null,
      status: "Draft",
      slots,
      notionPageId: null,
    };

    try {
      await onCreate(sessionDoc);
    } finally {
      setCreating(false);
    }
  };

  const focusBorder = "1px solid rgba(99,102,241,0.55)";
  const focusShadow = "0 0 0 3px rgba(99,102,241,0.12)";

  return (
    <div style={{ maxWidth: "540px" }}>
      <div style={{ ...card, padding: "32px" }}>
        {/* Header */}
        <div style={{ marginBottom: "28px" }}>
          <div style={{ fontSize: "18px", fontWeight: "700", color: C.text1, letterSpacing: "-0.02em", marginBottom: "6px" }}>
            Create New Session
          </div>
          <div style={{ fontSize: "13px", color: C.text3, lineHeight: "1.6" }}>
            A session is a named workspace for one recording set. Files uploaded here are permanently linked to this session ID.
          </div>
        </div>

        {/* Session Title */}
        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Session Title <span style={{ color: C.danger }}>*</span></label>
          <input
            type="text"
            placeholder="e.g. Japanese Milk Bread Making"
            value={title}
            onChange={e => setTitle(e.target.value)}
            onFocus={() => setFocusedField("title")}
            onBlur={() => setFocusedField(null)}
            onKeyDown={e => e.key === "Enter" && canCreate && handleCreate()}
            style={{
              ...inputStyle,
              ...(focusedField === "title" ? { border: focusBorder, boxShadow: focusShadow } : title.trim() ? { borderColor: "rgba(99,102,241,0.4)" } : {}),
            }}
          />
          <div style={{ fontSize: "11px", color: C.text3, marginTop: "5px" }}>
            This becomes the Video ID and is used in all auto-generated file names.
          </div>
        </div>

        {/* Project */}
        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Project <span style={{ color: C.danger }}>*</span></label>
          <select
            className="filter-select"
            value={project}
            onChange={e => {
              const next = e.target.value;
              setProject(next);
              if (next && !userEditedNumber && getDefaultSubmissionNumber) {
                setSubmissionNumber(String(getDefaultSubmissionNumber(next)));
              }
            }}
            onFocus={() => setFocusedField("project")}
            onBlur={() => setFocusedField(null)}
            style={{
              ...inputStyle,
              color: project ? C.text1 : C.text3,
              cursor: "pointer",
              ...(focusedField === "project" ? { border: focusBorder, boxShadow: focusShadow } : project ? { borderColor: "rgba(99,102,241,0.4)" } : {}),
            }}
          >
            <option value="" disabled>Select a project…</option>
            {projects.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* Outdoor Guide uses a different flow — redirect instead of the
            session form (no co-creator / slots / submission number). */}
        {isOutdoor && (
          <div style={{
            padding: "20px",
            background: "rgba(99,102,241,0.05)",
            border: "1px solid rgba(99,102,241,0.15)",
            borderRadius: "12px",
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: C.text1, marginBottom: "6px" }}>
              Outdoor Guide isn't a session
            </div>
            <div style={{ fontSize: "13px", color: C.text2, lineHeight: "1.6", marginBottom: "16px" }}>
              Outdoor Guide recordings aren't organized as sessions with fixed
              slots. Instead, you'll create a <strong>location</strong>, add one
              or more <strong>tasks</strong> (1–8), and under each task add{" "}
              <strong>clips</strong> — each clip holding one video and one or
              more images.
            </div>
            <Btn variant="primary" onClick={onOpenOutdoor}>Open Outdoor Guide →</Btn>
          </div>
        )}

        {!isOutdoor && (<>

        {/* Co-creator PID */}
        <div style={{ marginBottom: "28px" }}>
          <label style={labelStyle}>Co-creator PID <span style={{ color: C.text3, fontWeight: "400", textTransform: "none", letterSpacing: "normal" }}>(optional)</span></label>
          <input
            type="text"
            placeholder="e.g. P163"
            value={secondCreatorPid}
            onChange={e => { setSecondCreatorPid(e.target.value); setSecondCreatorError(""); }}
            onFocus={() => setFocusedField("secondCreator")}
            onBlur={() => setFocusedField(null)}
            style={{
              ...inputStyle,
              ...(secondCreatorError ? { border: "1px solid rgba(248,113,113,0.5)" } : focusedField === "secondCreator" ? { border: focusBorder, boxShadow: focusShadow } : {}),
            }}
          />
          {secondCreatorError
            ? <div style={{ fontSize: "11px", color: C.danger, marginTop: "5px" }}>{secondCreatorError}</div>
            : <div style={{ fontSize: "11px", color: C.text3, marginTop: "5px" }}>The PID of a co-creator on this recording. Used to credit both contributors on the data record.</div>
          }
          {/* Auto-share notice — entering a co-creator shares the session. */}
          {secondCreatorPid.trim() && !secondCreatorError && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginTop: "12px", padding: "10px 12px", background: "var(--primary-soft)", border: "1px solid rgba(99,102,241,0.18)", borderRadius: "8px" }}>
              <span aria-hidden="true" style={{ width: "7px", height: "7px", borderRadius: "50%", background: C.primary, flexShrink: 0, marginTop: "5px" }} />
              <span style={{ fontSize: "12px", color: C.text2, lineHeight: "1.5" }}>
                This session will be shared with them automatically — it'll appear on their dashboard right away and they can upload their own files.
              </span>
            </div>
          )}
        </div>

        {/* Submission Number */}
        <div style={{ marginBottom: "28px" }}>
          <label style={labelStyle}>Submission Number <span style={{ color: C.text3, fontWeight: "400", textTransform: "none", letterSpacing: "normal" }}>(auto · editable)</span></label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="e.g. 301"
            value={submissionNumber}
            onChange={e => { setSubmissionNumber(e.target.value.replace(/[^0-9]/g, "")); setUserEditedNumber(true); }}
            onFocus={() => setFocusedField("submissionNumber")}
            onBlur={() => setFocusedField(null)}
            style={{
              ...inputStyle,
              ...(focusedField === "submissionNumber" ? { border: focusBorder, boxShadow: focusShadow } : {}),
            }}
          />
          <div style={{ fontSize: "11px", color: C.text3, marginTop: "8px", lineHeight: "1.6" }}>
            Submission ID preview:{" "}
            <span style={{ fontFamily: "monospace", color: C.text2, background: "var(--surface-2)", padding: "2px 6px", borderRadius: "4px" }}>
              {project
                ? `${projectCode(project)}_P${stripPidPrefix(pid)}_P${(stripPidPrefix(secondCreatorPid) || stripPidPrefix(pid))}_${submissionNumber || "<num>"}`
                : "select a project to preview"}
            </span>
            <div style={{ marginTop: "4px" }}>Leave blank to auto-number based on already-submitted tasks in this project.</div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: "1px", background: "var(--border-subtle)", marginBottom: "24px" }} />

        {/* Session ID preview */}
        <div style={{
          padding: "10px 14px",
          background: "rgba(99,102,241,0.05)",
          border: "1px solid rgba(99,102,241,0.12)",
          borderRadius: "8px",
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}>
          <span style={{ fontSize: "11px", color: C.text3, flexShrink: 0 }}>Required files for all sessions:</span>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {REQUIRED_SLOTS.map(s => (
              <span key={s} style={{ fontSize: "11px", color: "var(--og-accent)", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)", padding: "2px 8px", borderRadius: "4px", fontWeight: "600" }}>
                {slotLabel(project, s)}
              </span>
            ))}
          </div>
        </div>

        {/* Create button */}
        <Btn
          variant="primary"
          onClick={handleCreate}
          disabled={!canCreate}
          loading={creating}
          style={{ width: "100%", padding: "13px" }}
        >
          {creating ? (secondCreatorPid.trim() ? "Validating PID…" : "Creating session…") : "Create Session →"}
        </Btn>

        </>)}
      </div>
    </div>
  );
}

export default SessionCreator;
